// Install page — bilingual Add-to-Home-Screen wizard.
// Auto-detects platform:
//   • iPhone Safari   → 3 step instruction cards (no native prompt available)
//   • Android Chrome  → native beforeinstallprompt button (or "already installed" state)
//   • Other browsers  → unsupported state with guidance
//
// Path: /install
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Share, Plus, Check, Smartphone, ArrowDown, Download, MoreHorizontal,
} from "lucide-react";
import { BottomNav } from "../components/BottomNav";
import { useI18n } from "../lib/i18n";
import { canInstall, promptInstall, subscribeInstall } from "../lib/pwa";

function detectPlatform() {
  if (typeof window === "undefined") return "other";
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  if (isIOS && isSafari) return "ios-safari";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true
  );
}

export default function Install() {
  const { t } = useI18n();
  const nav = useNavigate();
  const platform = useMemo(() => detectPlatform(), []);
  const [installable, setInstallable] = useState(canInstall());
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const unsub = subscribeInstall(setInstallable);
    const handler = () => setInstalled(isStandalone());
    window.addEventListener("appinstalled", handler);
    return () => { unsub(); window.removeEventListener("appinstalled", handler); };
  }, []);

  const handleInstall = async () => {
    const result = await promptInstall();
    if (result === "accepted") setInstalled(true);
  };

  return (
    <main data-testid="install-page" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
      <header className="px-5 pt-6 flex items-start gap-3">
        <button
          type="button"
          data-testid="install-back"
          onClick={() => nav(-1)}
          className="grid place-items-center rounded-full hover:bg-white/[0.04] transition-colors"
          style={{ minHeight: 48, minWidth: 48, color: "#F5F1E8" }}
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <div className="flex-1 min-w-0 pt-1">
          <div style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            Habano
          </div>
          <h1 className="mt-1" style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#F5F1E8", lineHeight: 1.15 }}>
            {platform === "ios-safari" ? t("install_ios_title") : platform === "android" ? t("install_android_title") : t("install_unavailable_t")}
          </h1>
          <div className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3", lineHeight: 1.5 }}>
            {platform === "ios-safari" ? t("install_ios_sub") : platform === "android" ? t("install_android_sub") : t("install_unavailable_s")}
          </div>
        </div>
      </header>

      {installed ? (
        <section className="px-5 mt-8" data-testid="install-already">
          <div
            className="text-center"
            style={{
              background: "rgba(34,197,94,0.06)",
              border: "1px solid rgba(34,197,94,0.3)",
              borderRadius: 16,
              padding: 24,
            }}
          >
            <Check className="mx-auto" style={{ width: 36, height: 36, color: "#22c55e" }} strokeWidth={1.5} />
            <h2 className="mt-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#F5F1E8" }}>
              {t("install_already")}
            </h2>
          </div>
        </section>
      ) : platform === "ios-safari" ? (
        <IOSGuide t={t} onClose={() => nav("/journey")} />
      ) : platform === "android" ? (
        <AndroidGuide t={t} installable={installable} onInstall={handleInstall} />
      ) : (
        <OtherGuide t={t} />
      )}

      <BottomNav />
    </main>
  );
}

function IOSGuide({ t, onClose }) {
  const steps = [
    { icons: [Share, MoreHorizontal], label: t("install_ios_step_1") },
    { icons: [Plus], label: t("install_ios_step_2") },
    { icons: [Check], label: t("install_ios_step_3") },
  ];
  return (
    <>
      <section className="px-5 mt-6 space-y-3" data-testid="install-ios-steps">
        {steps.map((s, i) => (
          <StepCard key={i} index={i + 1} icons={s.icons} label={s.label} />
        ))}
      </section>

      <section className="px-5 mt-6 flex flex-col items-center" data-testid="install-ios-pointer">
        <ArrowDown
          className="text-gold animate-bounce"
          style={{ width: 36, height: 36 }}
          strokeWidth={1.5}
        />
        <div
          className="mt-2"
          style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3", letterSpacing: "0.16em", textTransform: "uppercase" }}
        >
          Safari Share
        </div>
      </section>

      <section className="px-5 mt-8">
        <button
          type="button"
          data-testid="install-ios-got-it"
          onClick={onClose}
          className="w-full inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
          style={{
            minHeight: 48,
            background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
            color: "#0B0B0B",
            borderRadius: 12,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            border: "none",
          }}
        >
          {t("install_ios_got_it")}
        </button>
      </section>
    </>
  );
}

function AndroidGuide({ t, installable, onInstall }) {
  return (
    <section className="px-5 mt-8 space-y-4" data-testid="install-android">
      <div className="text-center">
        <div
          className="mx-auto grid place-items-center"
          style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(232,184,75,0.12)", border: "1px solid rgba(232,184,75,0.35)" }}
        >
          <Smartphone style={{ width: 36, height: 36, color: "#E8B84B" }} strokeWidth={1.5} />
        </div>
      </div>

      <button
        type="button"
        data-testid="install-android-cta"
        onClick={onInstall}
        disabled={!installable}
        className="w-full inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
        style={{
          minHeight: 56,
          background: installable ? "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)" : "#141414",
          color: installable ? "#0B0B0B" : "#B0AAA3",
          border: installable ? "none" : "1px solid rgba(232,184,75,0.35)",
          borderRadius: 12,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
        }}
      >
        <Download className="w-4 h-4" strokeWidth={1.5} />
        {t("install_android_cta")}
      </button>

      {!installable && (
        <p className="text-center" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#B0AAA3", lineHeight: 1.5 }}>
          {t("install_unavailable_s")}
        </p>
      )}
    </section>
  );
}

function OtherGuide({ t }) {
  return (
    <section className="px-5 mt-8 space-y-4" data-testid="install-other">
      <div className="text-center">
        <div
          className="mx-auto grid place-items-center"
          style={{ width: 72, height: 72, borderRadius: 18, background: "rgba(232,184,75,0.10)", border: "1px solid rgba(232,184,75,0.3)" }}
        >
          <Smartphone style={{ width: 32, height: 32, color: "#E8B84B" }} strokeWidth={1.5} />
        </div>
        <p className="mt-4 mx-auto max-w-[300px]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3", lineHeight: 1.6 }}>
          {t("install_unavailable_s")}
        </p>
      </div>
      <Link
        to="/journey"
        className="block w-full text-center"
        style={{
          minHeight: 48,
          padding: "14px 16px",
          background: "transparent",
          border: "1px solid rgba(232,184,75,0.5)",
          color: "#E8B84B",
          borderRadius: 12,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        {t("install_ios_got_it")}
      </Link>
    </section>
  );
}

function StepCard({ index, icons, label }) {
  return (
    <div
      data-testid={`install-step-${index}`}
      style={{
        background: "#141414",
        border: "1px solid rgba(232,184,75,0.25)",
        borderRadius: 14,
        padding: 14,
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <span
        className="grid place-items-center shrink-0"
        style={{
          width: 40, height: 40, borderRadius: 999,
          background: "rgba(232,184,75,0.12)", border: "1px solid rgba(232,184,75,0.4)",
          color: "#E8B84B", fontFamily: "'Playfair Display', serif", fontSize: 18,
        }}
      >
        {index}
      </span>
      <div className="shrink-0 flex items-center gap-1.5">
        {(icons || []).map((Ic, i) => (
          <Ic key={i} style={{ width: 20, height: 20, color: "#E8B84B" }} strokeWidth={1.5} />
        ))}
      </div>
      <div className="flex-1 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8", lineHeight: 1.4 }}>
        {label}
      </div>
    </div>
  );
}
